<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content">
        <section class="intro">
          <p>
            Adding a virtual tour to your WordPress website is easier than you
            think. Whether you're a real estate agent, hotel owner, or business,
            embedding an interactive 360° tour takes just a few minutes and no
            coding knowledge. Here's the complete step-by-step guide.
          </p>
        </section>

        <section>
          <h2>Method 1 — Embed via Iframe (Recommended)</h2>
          <p>
            This works on any WordPress site and requires no plugin. When you
            publish a tour in Travvir, you receive an embed code — a standard
            HTML iframe snippet. To add it to your WordPress page:
          </p>
          <p>
            1. Open the WordPress block editor for the page you want to add the
            tour to. 2. Add a "Custom HTML" block. 3. Paste the Travvir iframe
            embed code into the block. 4. Preview the page to confirm the tour
            displays correctly. 5. Publish.
          </p>
          <p>
            The iframe embed works with the WordPress Classic Editor, Gutenberg,
            Elementor, Divi, and all major page builders — anywhere you can add
            a Custom HTML block or widget.
          </p>
        </section>

        <section>
          <h2>Method 2 — WordPress Virtual Tour Plugin</h2>
          <p>
            Several WordPress plugins support 360° virtual tour embedding.
            Popular options include WP VR (for local hosting of equirectangular
            images) and Virtual Tour Easy (iframe manager). These plugins add a
            user-friendly interface for managing multiple embedded tours across
            your site.
          </p>
          <h3>When to Use a Plugin</h3>
          <p>
            Use a plugin if: you have multiple tours embedded across many pages
            and need centralised management, you want to host tour images
            locally on your server rather than using a third-party platform, or
            you need to manage tour permissions by user role.
          </p>
        </section>

        <section>
          <h2>Method 3 — Shortcode Embedding</h2>
          <p>
            If your WordPress theme supports shortcodes in content areas, you
            can create a simple shortcode wrapper: add the iframe code as a
            shortcode value in your functions.php or via a code snippet plugin.
            This is useful for real estate sites where tour embeds appear in
            automated listing templates.
          </p>
        </section>

        <section>
          <h2>Best Practices for WordPress Virtual Tour Embeds</h2>
          <p>
            Set iframe height to at least 500px for desktop viewing. Use a
            ratio-responsive wrapper CSS for mobile (the tour should scale
            properly on all devices). Add a fallback message inside the iframe
            tags for browsers that block iframes. Place the tour above the fold
            on property detail pages — the higher it appears, the longer viewers
            stay on the page.
          </p>
        </section>
        <section>
          <h2>Continue Reading</h2>
          <ul>
          <li><a href="/blog/en/embed-360-virtual-tour-website">Embed Tours on Any Website</a></li>
          <li><a href="/blog/en/virtual-tour-seo-guide">Virtual Tour SEO Guide</a></li>
          <li><a href="/blog/en/add-virtual-tour-to-listing">Add Tour to Listing</a></li>
          <li><a href="/blog/en/virtual-tour-small-business">Tours for Small Business</a></li>
          <li><a href="/blog/en/free-virtual-tour-creator">Complete 360 Tour Guide</a></li>
          </ul>
        </section>


        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";
const postData = blogPostsData.find(
  (p) => p.slug === "embed-virtual-tour-wordpress",
);

useBlogSeo({
  title: "Embed a Virtual Tour in WordPress [5-Minute Guide] | Travvir",
  description:
    "Embed a virtual tour on your WordPress site in under 5 minutes. Step-by-step instructions for iframe, shortcode, and plugin methods.",
  keywords:
    "embed virtual tour wordpress, virtual tour wordpress, add virtual tour wordpress, wordpress 360 tour, virtual tour iframe wordpress",
  slug: "embed-virtual-tour-wordpress",
  image: postData.image,
  postData,
});
</script>
